import {
  REACT_APP_APPLICATION_CODE,
  REACT_APP_API_BASE_URL_LOGIN,
} from "constants/index";

import { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Stack,
  Step,
  StepLabel,
  Stepper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowNarrowLeftIcon,
  Button,
  ClockFastForwardIcon,
  FormContainerControlled,
  Lock01Icon,
  Mail01Icon,
  TextFieldHookForm,
  Typography,
} from "components";
import OTPInputHookForm from "components/OTPInput/OTPInput";
import { QontoConnector } from "components/Step/Step";
import { base, gray, primary } from "configs/colors";
import { shadows } from "configs/shadows";
import { loginTSM } from "features/auth/api/login";
import {
  confirmPasswordRequired,
  emailRequired,
  otpRequired,
  resetPasswordRequired,
} from "helpers/yup";
import { loadPermission, loadPermissionMenu } from "libs/auth";
import { useForm } from "react-hook-form";
import storage from "utils/storage";
import * as Yup from "yup";

import content from "../assets/images/content.png";
import LoginFrom from "../LoginForm";
import {
  useChangePasswordV2,
  useGetMe,
  useGetOTP,
  useLoginOTP,
} from "../query";
import {
  ContentImage,
  FormContainer,
  LeftSection,
  PageContainer,
  RightSection,
} from "../styles";

interface GetOTPInput {
  email: string;
}

interface LoginOTPInput {
  code: number;
}

interface ChangePasswordInput {
  password: string;
}
const formGetOTP = Yup.object({
  email: emailRequired,
});
const formChangePassword = Yup.object({
  newPassword: resetPasswordRequired,
  password: confirmPasswordRequired,
  otpCode: otpRequired,
});

const LoginOTPFrom = () => {
  const [token, setToken] = useState("");
  const [activeStep, setActiveStep] = useState(1);
  const [timeCount, setTimeCount] = useState<number>(0);
  const [csrfToken, setCsrfToken] = useState<string>("");
  const [isLoginPassword, setIsLoginPassword] = useState<boolean>(false);

  const theme = useTheme();
  const mdUpMatched = useMediaQuery(theme.breakpoints.up("md"));
  const getOTPMethods = useForm<GetOTPInput | any>({
    resolver: yupResolver(formGetOTP),
  });
  const { watch } = getOTPMethods;

  const loginOTPMethods = useForm<any>({});
  const { watch: watchLoginOTP, reset: resetFormLoginOTP } = loginOTPMethods;

  const changePasswordMethods = useForm<any>({
    resolver: yupResolver(formChangePassword),
  });
  const { watch: watchChangePassword, reset: resetFormChangePassword } =
    changePasswordMethods;

  const {
    mutate: getOTP,
    isLoading,
    error: errorGetOTP,
  } = useGetOTP({
    axios: {
      baseURL: REACT_APP_API_BASE_URL_LOGIN,
      headers: {
        "x-application-code": REACT_APP_APPLICATION_CODE,
      },
    },
  });
  // eslint-disable-next-line
  const { data: dataMe, isLoading: isLoadingGetMe } = useGetMe({
    axios: {
      baseURL: REACT_APP_API_BASE_URL_LOGIN,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
    query: {
      enabled: !!token,
      async onSuccess(data) {
        if (data?.data?.data?.hasSecretKey) {
          storage.setToken(token);
          await loginTSM();
          await loadPermission(token);
          await loadPermissionMenu(token);
          window.location.reload();
        } else {
          getOTP(
            {
              data: {
                email: data?.data?.data?.email,
              },
            },
            {
              onSuccess(dataGetOTP) {
                setCsrfToken(dataGetOTP?.data?.data?.csrfToken as string);
              },
            }
          );
          setActiveStep(3);
        }
      },
    },
  });

  const {
    mutate: loginOTP,
    isLoading: isLoadingLoginOTP,
    error: errorLogin,
    reset: resetLoginOTP,
  } = useLoginOTP({
    axios: {
      baseURL: REACT_APP_API_BASE_URL_LOGIN,
      headers: {
        "x-application-code": REACT_APP_APPLICATION_CODE,
      },
    },
  });

  const {
    mutate: changePassword,
    isLoading: isLoadingChangePassword,
    error: errChangePassword,
  } = useChangePasswordV2({
    axios: {
      baseURL: REACT_APP_API_BASE_URL_LOGIN,
      headers: {
        Authorization: `Bearer ${token}`,
        "x-application-code": REACT_APP_APPLICATION_CODE,
        "x-organization-id": dataMe?.data?.data?.organizations
          ? (dataMe?.data?.data?.organizations as any[])[0]?.id
          : undefined,
        "x-tenant-code": dataMe?.data?.data?.organizations
          ? (dataMe?.data?.data?.organizations as any[])[0]?.tenantCode
          : undefined,
      },
    },
  });

  const onSubmit = (data: GetOTPInput) => {
    getOTP(
      {
        data: {
          email: data?.email,
        },
      },
      {
        onSuccess(dataGetOTP) {
          setActiveStep(2);
          setTimeCount(dataGetOTP?.data?.data?.remainSecondExpires as number);
          setCsrfToken(dataGetOTP?.data?.data?.csrfToken as string);
        },
      }
    );
  };

  const onSubmitOTP = (data: LoginOTPInput) => {
    loginOTP(
      {
        data: {
          provider: "otp",
          authData: {
            code: Number(data?.code),
            csrfToken,
            email: watch("email"),
          },
        },
      },
      {
        onSuccess(dataLogin) {
          setToken(dataLogin?.data?.data?.accessToken as string);
        },
      }
    );
  };

  const onSubmitChangePassword = (data: ChangePasswordInput) => {
    if (
      dataMe?.data?.data?.organizations &&
      dataMe?.data?.data?.organizations?.length > 0
    ) {
      changePassword(
        {
          data: {
            code: Number(watchChangePassword("otpCode")),
            csrfToken,
            password: data?.password,
          },
        },
        {
          async onSuccess() {
            storage.setToken(token);
            await loginTSM();
            await loadPermission(token);
            await loadPermissionMenu(token);
            window.location.reload();
          },
        }
      );
    }
  };

  useEffect(() => {
    const myInterval = setInterval(() => {
      if (timeCount > 0 && activeStep === 2) {
        setTimeCount(timeCount - 1);
      }
    }, 1000);
    return () => {
      clearInterval(myInterval);
    };
  }, [activeStep, timeCount, setTimeCount]);

  return isLoginPassword ? (
    <LoginFrom setIsLoginPassword={setIsLoginPassword} />
  ) : (
    <PageContainer>
      <LeftSection mdUpMatched={mdUpMatched}>
        <Box
          margin={2}
          flex={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <FormContainer>
            {activeStep === 1 ? (
              <FormContainerControlled
                methods={getOTPMethods}
                onSubmit={onSubmit}
              >
                <Typography type="DisplayMD" fontWeight={600} color={gray[900]}>
                  Log in
                </Typography>
                <Typography
                  type="TextMD"
                  color={gray[600]}
                  marginTop={3}
                  marginBottom={8}
                >
                  Welcome back! Please enter your credential.
                </Typography>
                <Stack gap={6}>
                  <Stack gap={5}>
                    <TextFieldHookForm
                      label="Email"
                      placeholder="Enter your email"
                      name="email"
                      required
                    />
                  </Stack>
                  {errorGetOTP ? (
                    <Stack gap={5}>
                      <Typography type="TextSM" fontWeight={600} color="red">
                        The email address you entered is not in our system.
                        Please try again with a different email address!
                      </Typography>
                    </Stack>
                  ) : undefined}
                  <Button
                    size="lg"
                    useTypography
                    type="submit"
                    loading={isLoading || getOTPMethods.formState.isSubmitting}
                    sx={{
                      ".MuiTypography-root": {
                        fontSize: "14px",
                        lineHeight: "20px",
                      },
                      padding: "10px 16px",
                    }}
                  >
                    {isLoading || getOTPMethods.formState.isSubmitting
                      ? "Loading ..."
                      : "Get OTP"}
                  </Button>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  marginY="32px"
                >
                  <Typography
                    type="TextSM"
                    fontWeight={600}
                    color={primary[700]}
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setIsLoginPassword(true);
                    }}
                  >
                    Login with Password
                  </Typography>
                </Stack>
              </FormContainerControlled>
            ) : null}
            {activeStep === 2 ? (
              <FormContainerControlled
                methods={loginOTPMethods}
                onSubmit={onSubmitOTP}
              >
                <Box display="flex" justifyContent="center" marginBottom="24px">
                  <Box
                    display="flex"
                    borderRadius="8px"
                    justifyContent="center"
                    border={`1px solid ${gray[300]}`}
                    boxShadow={shadows.xs}
                    sx={{ background: base.White }}
                    padding="14px"
                    width="56px"
                    height="56px"
                  >
                    <Mail01Icon />
                  </Box>
                </Box>
                <Typography
                  type="DisplaySM"
                  fontWeight={600}
                  color={gray[900]}
                  align="center"
                >
                  Check your email
                </Typography>
                <Typography
                  type="TextMD"
                  color={gray[600]}
                  marginTop={3}
                  marginY="12px"
                  align="center"
                >
                  We sent an One Time Password to
                </Typography>
                <Typography
                  type="TextMD"
                  color={gray[600]}
                  marginTop={3}
                  align="center"
                >
                  {watch("email")}
                </Typography>
                <Stack gap={6}>
                  <OTPInputHookForm name="code" />
                </Stack>
                <Stack>
                  <Button
                    size="lg"
                    useTypography
                    // sx={{ background: primary[50] }}
                    type="submit"
                    loading={
                      isLoadingLoginOTP ||
                      loginOTPMethods.formState.isSubmitting
                    }
                    disabled={watchLoginOTP("code")?.length !== 6}
                    sx={{
                      ".MuiTypography-root": {
                        fontSize: "14px",
                        lineHeight: "20px",
                      },
                      padding: "10px 16px",
                    }}
                  >
                    {isLoadingLoginOTP || loginOTPMethods.formState.isSubmitting
                      ? "Loading ..."
                      : "Verify email"}
                  </Button>
                </Stack>

                {errorLogin ? (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    marginY="32px"
                  >
                    <Box>
                      <Typography type="TextSM" fontWeight={600} color="red">
                        Your OTP code is incorrect or expired! Please enter it
                        again.
                      </Typography>
                    </Box>
                  </Stack>
                ) : null}

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  marginY="32px"
                >
                  <Box>
                    <Typography
                      type="TextSM"
                      fontWeight={400}
                      // color={primary[700]}
                    >
                      Didn’t receive the email?
                    </Typography>
                  </Box>
                  <Box>
                    {timeCount === 0 ? (
                      <Typography
                        type="TextSM"
                        fontWeight={600}
                        color={primary[700]}
                        onClick={() => {
                          onSubmit(watch() as GetOTPInput);
                        }}
                        sx={{
                          cursor: "pointer",
                        }}
                      >
                        &nbsp;Click to resend
                      </Typography>
                    ) : (
                      <Box display="flex">
                        <Typography
                          type="TextSM"
                          fontWeight={600}
                          color={primary[400]}
                        >
                          &nbsp;
                          {`Click to resend after ${timeCount}s`}
                        </Typography>
                        <ClockFastForwardIcon
                          fontSize="small"
                          sx={{
                            color: primary[400],
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Stack>

                <Box display="flex" justifyContent="center">
                  <Button
                    size="small"
                    useTypography
                    variant="text"
                    startIcon={<ArrowNarrowLeftIcon />}
                    color="inherit"
                    sx={{
                      fontSize: "14px",
                    }}
                    onClick={() => {
                      setActiveStep(1);
                      resetFormLoginOTP();
                      resetLoginOTP();
                    }}
                  >
                    Back to log in
                  </Button>
                </Box>
              </FormContainerControlled>
            ) : null}
            {activeStep === 3 ? (
              <FormContainerControlled
                methods={changePasswordMethods}
                onSubmit={onSubmitChangePassword}
              >
                <Box display="flex" justifyContent="center" marginBottom="24px">
                  <Box
                    display="flex"
                    borderRadius="8px"
                    border={`1px solid ${gray[300]}`}
                    boxShadow={shadows.xs}
                    sx={{ background: base.White }}
                    padding="14px"
                    width="56px"
                  >
                    <Lock01Icon />
                  </Box>
                </Box>

                <Typography
                  type="DisplayMD"
                  fontWeight={600}
                  color={gray[900]}
                  align="center"
                >
                  Set your Password
                </Typography>
                <Typography
                  type="TextMD"
                  color={gray[600]}
                  marginTop={3}
                  marginY="12px"
                  align="center"
                >
                  Please set password for account
                </Typography>
                <Typography
                  type="TextMD"
                  color={gray[600]}
                  marginTop={3}
                  align="center"
                >
                  {watch("email")}
                </Typography>
                <Stack gap={6} marginBottom={8}>
                  <TextFieldHookForm
                    required
                    label="Password"
                    name="newPassword"
                    placeholder="Create a password"
                    type="password"
                    helperText="Must have at least 8 characters"
                  />
                </Stack>
                <Stack gap={6} marginBottom={8}>
                  <TextFieldHookForm
                    required
                    label="Confirm Password"
                    name="password"
                    placeholder="Confirm Password"
                    type="password"
                    helperText="Must have at least 8 characters"
                  />
                </Stack>
                <Stack gap={6} marginBottom={8}>
                  <TextFieldHookForm
                    required
                    label="OTP Code"
                    name="otpCode"
                    placeholder="Enter OTP"
                    helperText="Please check your email for the OTP code to update your new password!"
                  />
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  marginY="32px"
                >
                  {errChangePassword ? (
                    <Box>
                      <Typography type="TextSM" fontWeight={600} color="red">
                        Your OTP code is incorrect or expired! Please enter it
                        again.
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
                <Stack marginBottom={8}>
                  <Button
                    size="lg"
                    useTypography
                    type="submit"
                    loading={
                      isLoadingChangePassword ||
                      changePasswordMethods.formState.isSubmitting
                    }
                    sx={{
                      ".MuiTypography-root": {
                        fontSize: "14px",
                        lineHeight: "20px",
                      },
                      padding: "10px 16px",
                    }}
                  >
                    {isLoadingChangePassword ||
                    changePasswordMethods.formState.isSubmitting
                      ? "Loading ..."
                      : "Continue"}
                  </Button>
                </Stack>

                <Box display="flex" justifyContent="center">
                  <Button
                    size="small"
                    useTypography
                    variant="text"
                    startIcon={<ArrowNarrowLeftIcon />}
                    color="inherit"
                    sx={{
                      fontSize: "14px",
                    }}
                    onClick={() => {
                      setActiveStep(2);
                      resetFormLoginOTP();
                      setTimeCount(0);
                      resetFormChangePassword();
                    }}
                  >
                    Back to log in
                  </Button>
                </Box>
              </FormContainerControlled>
            ) : null}
            <Stack marginTop="24px">
              <Stepper
                alternativeLabel
                activeStep={activeStep}
                connector={<QontoConnector />}
              >
                {[1, 2, 3, 4].map((label) => (
                  <Step key={label}>
                    <StepLabel StepIconComponent={() => null} />
                  </Step>
                ))}
              </Stepper>
            </Stack>
          </FormContainer>
        </Box>
        <Box padding={mdUpMatched ? 8 : 2}>
          <Typography
            type="TextSM"
            color={mdUpMatched ? gray[600] : primary[200]}
          >
            © Talentnet 2023
          </Typography>
        </Box>
      </LeftSection>
      {mdUpMatched ? (
        <RightSection>
          <ContentImage src={content} alt="Content" />
        </RightSection>
      ) : null}
    </PageContainer>
  );
};

export default LoginOTPFrom;
